import React from "react";
import {
  Timer,
  CheckCircle,
  Activity,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  Clock,
  Server,
  Network,
  Shield,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { AIAgent } from "./types";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// Mock data for demonstration - in real app this would come from the agent
const mockApiPerformance = {
  latencyOverTime: [
    {
      timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      latency: 850,
      requestCount: 150,
    },
    {
      timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
      latency: 920,
      requestCount: 180,
    },
    {
      timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      latency: 780,
      requestCount: 200,
    },
    {
      timestamp: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
      latency: 1100,
      requestCount: 220,
    },
    {
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      latency: 950,
      requestCount: 250,
    },
    {
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      latency: 1200,
      requestCount: 280,
    },
    {
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      latency: 1350,
      requestCount: 300,
    },
    { timestamp: new Date(), latency: 1450, requestCount: 320 },
  ],
  latencyByRequestCount: [
    {
      requestCount: 50,
      averageLatency: 650,
      p95Latency: 1200,
      p99Latency: 1800,
    },
    {
      requestCount: 100,
      averageLatency: 850,
      p95Latency: 1500,
      p99Latency: 2200,
    },
    {
      requestCount: 200,
      averageLatency: 1100,
      p95Latency: 1800,
      p99Latency: 2800,
    },
    {
      requestCount: 300,
      averageLatency: 1400,
      p95Latency: 2200,
      p99Latency: 3500,
    },
  ],
  successRateByHour: Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    successRate: 85 + Math.random() * 15,
    totalRequests: Math.floor(50 + Math.random() * 100),
    failedRequests: Math.floor(5 + Math.random() * 20),
  })),
  errorBreakdown: {
    timeoutErrors: 45,
    rateLimitErrors: 12,
    authenticationErrors: 8,
    serverErrors: 23,
    networkErrors: 15,
    totalErrors: 103,
  },
  throughputMetrics: {
    requestsPerMinute: 12.5,
    requestsPerHour: 750,
    peakRequestsPerMinute: 25,
    averageConcurrentRequests: 8,
  },
  costAnalysis: {
    costPerRequest: 0.0025,
    totalCost: 125.5,
    costByEndpoint: [
      { endpoint: "/api/chat", requests: 2500, cost: 75.0 },
      { endpoint: "/api/completions", requests: 1500, cost: 35.0 },
      { endpoint: "/api/embeddings", requests: 800, cost: 15.5 },
    ],
  },
};

const mockRecommendations = [
  {
    id: "1",
    type: "optimization" as const,
    title: "Implement Request Caching",
    description:
      "Cache frequently requested responses to reduce latency by 40%",
    impact: "high" as const,
    priority: "high" as const,
    implementationEffort: "medium" as const,
  },
  {
    id: "2",
    type: "scaling" as const,
    title: "Add Rate Limiting",
    description:
      "Implement intelligent rate limiting to prevent API throttling and improve reliability",
    impact: "medium" as const,
    priority: "medium" as const,
    implementationEffort: "easy" as const,
  },
  {
    id: "3",
    type: "optimization" as const,
    title: "Optimize Token Usage",
    description:
      "Reduce average tokens per request by 15% through prompt optimization",
    impact: "high" as const,
    priority: "critical" as const,
    implementationEffort: "hard" as const,
  },
  {
    id: "4",
    type: "reliability" as const,
    title: "Implement Circuit Breaker",
    description:
      "Add circuit breaker pattern to handle API failures gracefully",
    impact: "medium" as const,
    priority: "high" as const,
    implementationEffort: "medium" as const,
  },
];

const PerformanceMetrics: React.FC<{ agent: AIAgent }> = ({ agent }) => {
  const apiPerf = mockApiPerformance; // In real app: agent.performance.apiPerformance

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2">
            <Timer className="h-4 w-4 text-blue-400" />
            <span className="text-sm font-medium text-gray-300">
              Avg Latency
            </span>
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-gray-100">
              {Math.round(
                apiPerf.latencyOverTime[apiPerf.latencyOverTime.length - 1]
                  .latency,
              )}
              ms
            </div>
            <div className="flex items-center text-xs text-gray-400 mt-1">
              <TrendingUp className="h-3 w-3 text-red-400 mr-1" />
              <span>+12% from last week</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-green-400" />
            <span className="text-sm font-medium text-gray-300">
              Success Rate
            </span>
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-gray-100">
              {Math.round(agent.performance.successRate)}%
            </div>
            <Progress value={agent.performance.successRate} className="mt-2" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2">
            <Activity className="h-4 w-4 text-pink-400" />
            <span className="text-sm font-medium text-gray-300">
              Throughput
            </span>
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-gray-100">
              {apiPerf.throughputMetrics.requestsPerMinute.toFixed(1)}/min
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Peak: {apiPerf.throughputMetrics.peakRequestsPerMinute}/min
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const LatencyChart: React.FC = () => {
  const data = mockApiPerformance.latencyOverTime;

  const chartData = {
    labels: data.map((point) =>
      point.timestamp.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    ),
    datasets: [
      {
        label: "Latency (ms)",
        data: data.map((point) => point.latency),
        borderColor: "rgb(59, 130, 246)",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "rgb(59, 130, 246)",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgba(59, 130, 246, 0.5)",
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          color: "rgba(75, 85, 99, 0.2)",
        },
        ticks: {
          color: "#9ca3af",
        },
      },
      y: {
        grid: {
          color: "rgba(75, 85, 99, 0.2)",
        },
        ticks: {
          color: "#9ca3af",
        },
      },
    },
  };

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader>
        <CardTitle className="text-gray-100 flex items-center space-x-2">
          <TrendingUp className="h-5 w-5" />
          <span>Latency Over Time</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <Line data={chartData} options={options} />
        </div>
      </CardContent>
    </Card>
  );
};

const ErrorBreakdown: React.FC = () => {
  const errors = mockApiPerformance.errorBreakdown;
  const totalErrors = errors.totalErrors;

  const errorTypes = [
    {
      name: "Timeout",
      count: errors.timeoutErrors,
      color: "text-red-400",
      icon: Clock,
    },
    {
      name: "Rate Limit",
      count: errors.rateLimitErrors,
      color: "text-yellow-400",
      icon: AlertTriangle,
    },
    {
      name: "Auth",
      count: errors.authenticationErrors,
      color: "text-orange-400",
      icon: Shield,
    },
    {
      name: "Server",
      count: errors.serverErrors,
      color: "text-purple-400",
      icon: Server,
    },
    {
      name: "Network",
      count: errors.networkErrors,
      color: "text-blue-400",
      icon: Network,
    },
  ];

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader>
        <CardTitle className="text-gray-100 flex items-center space-x-2">
          <AlertTriangle className="h-5 w-5" />
          <span>Error Breakdown</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {errorTypes.map((error) => (
            <div key={error.name} className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <error.icon className={`h-4 w-4 ${error.color}`} />
                <span className="text-sm text-gray-300">{error.name}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-300">{error.count}</span>
                <div className="w-20 bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-red-500 h-2 rounded-full"
                    style={{ width: `${(error.count / totalErrors) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const SuccessRateChart: React.FC = () => {
  const data = mockApiPerformance.successRateByHour;

  const chartData = {
    labels: data.map((hour) => `${hour.hour.toString().padStart(2, "0")}:00`),
    datasets: [
      {
        label: "Success Rate (%)",
        data: data.map((hour) => hour.successRate),
        backgroundColor: "rgba(34, 197, 94, 0.8)",
        borderColor: "rgb(34, 197, 94)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgba(34, 197, 94, 0.5)",
        borderWidth: 1,
        callbacks: {
          label: function (context: any) {
            return `Success Rate: ${context.parsed.y.toFixed(1)}%`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: "rgba(75, 85, 99, 0.2)",
        },
        ticks: {
          color: "#9ca3af",
          maxTicksLimit: 12,
        },
      },
      y: {
        grid: {
          color: "rgba(75, 85, 99, 0.2)",
        },
        ticks: {
          color: "#9ca3af",
          callback: function (value: any) {
            return value + "%";
          },
        },
        min: 0,
        max: 100,
      },
    },
  };

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader>
        <CardTitle className="text-gray-100 flex items-center space-x-2">
          <BarChart3 className="h-5 w-5" />
          <span>Success Rate by Hour</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <Bar data={chartData} options={options} />
        </div>
      </CardContent>
    </Card>
  );
};

const LatencyByRequestCount: React.FC = () => {
  const data = mockApiPerformance.latencyByRequestCount;

  const chartData = {
    labels: data.map((item) => `${item.requestCount} req`),
    datasets: [
      {
        label: "Average Latency (ms)",
        data: data.map((item) => item.averageLatency),
        borderColor: "rgb(59, 130, 246)",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        fill: false,
        tension: 0.4,
        pointBackgroundColor: "rgb(59, 130, 246)",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
      },
      {
        label: "P95 Latency (ms)",
        data: data.map((item) => item.p95Latency),
        borderColor: "rgb(239, 68, 68)",
        backgroundColor: "rgba(239, 68, 68, 0.1)",
        fill: false,
        tension: 0.4,
        pointBackgroundColor: "rgb(239, 68, 68)",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
      },
      {
        label: "P99 Latency (ms)",
        data: data.map((item) => item.p99Latency),
        borderColor: "rgb(168, 85, 247)",
        backgroundColor: "rgba(168, 85, 247, 0.1)",
        fill: false,
        tension: 0.4,
        pointBackgroundColor: "rgb(168, 85, 247)",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          color: "#9ca3af",
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgba(59, 130, 246, 0.5)",
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          color: "rgba(75, 85, 99, 0.2)",
        },
        ticks: {
          color: "#9ca3af",
        },
      },
      y: {
        grid: {
          color: "rgba(75, 85, 99, 0.2)",
        },
        ticks: {
          color: "#9ca3af",
        },
      },
    },
  };

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader>
        <CardTitle className="text-gray-100 flex items-center space-x-2">
          <Activity className="h-5 w-5" />
          <span>Latency by Request Count</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <Line data={chartData} options={options} />
        </div>
      </CardContent>
    </Card>
  );
};

export const PerformanceTab: React.FC<{ agent: AIAgent }> = ({ agent }) => {
  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Performance Metrics */}
      <PerformanceMetrics agent={agent} />

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div className="h-64 lg:h-80">
          <LatencyChart />
        </div>
        <div className="h-64 lg:h-80">
          <SuccessRateChart />
        </div>
      </div>

      {/* Additional Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div className="h-64 lg:h-80">
          <LatencyByRequestCount />
        </div>
        <div className="h-64 lg:h-80">
          <ErrorBreakdown />
        </div>
      </div>
    </div>
  );
};
